import React from "react";

// Elegant line chart rendered as SVG
export default function LineChart({
  data = [],
  yMin,
  yMax,
  height = 240,
  accent = "#ffffff",
  showDots = false,
  unit = "",
  emptyMessage = "Nessun dato disponibile — connetti il tuo SmartDream per iniziare il monitoraggio",
}) {
  const padding = { top: 16, right: 16, bottom: 26, left: 36 };
  const w = 560;
  const h = height;
  const innerW = w - padding.left - padding.right;
  const innerH = h - padding.top - padding.bottom;

  const isEmpty = !data || data.length === 0;
  const values = isEmpty ? [] : data.map((d) => d.value);
  const minV = yMin ?? (isEmpty ? 0 : Math.min(...values));
  const maxV = yMax ?? (isEmpty ? 100 : Math.max(...values));
  const range = maxV - minV || 1;

  const stepsY = 5;
  const yTicks = Array.from({ length: stepsY + 1 }, (_, i) => {
    const v = minV + ((maxV - minV) * i) / stepsY;
    return Math.round(v * 10) / 10;
  });

  const xFor = (i) =>
    padding.left + (data.length > 1 ? (i / (data.length - 1)) * innerW : innerW / 2);
  const yFor = (v) => padding.top + innerH - ((v - minV) / range) * innerH;

  const path = isEmpty
    ? ""
    : data
        .map((d, i) => `${i === 0 ? "M" : "L"}${xFor(i)},${yFor(d.value)}`)
        .join(" ");
  const areaPath = isEmpty
    ? ""
    : `${path} L${xFor(data.length - 1)},${yFor(minV)} L${xFor(0)},${yFor(minV)} Z`;

  // x-axis sample labels (~6 ticks)
  const xTickCount = Math.min(6, Math.max(2, data.length));
  const xTicks = Array.from({ length: xTickCount }, (_, i) =>
    isEmpty ? null : Math.round((i / (xTickCount - 1)) * (data.length - 1))
  );

  return (
    <div
      className="rounded-[22px] overflow-hidden relative"
      style={{
        background:
          "linear-gradient(160deg, #0fa88a 0%, #0b7d6a 55%, #074a42 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
      }}
    >
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto block">
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* grid lines */}
        {yTicks.map((t, i) => {
          const y = yFor(t);
          return (
            <g key={i}>
              <line
                x1={padding.left}
                x2={w - padding.right}
                y1={y}
                y2={y}
                stroke="rgba(255,255,255,0.12)"
                strokeDasharray="3 4"
              />
              <text
                x={padding.left - 8}
                y={y + 3}
                fontSize="10"
                textAnchor="end"
                fill="rgba(255,255,255,0.75)"
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* Y-axis line */}
        <line
          x1={padding.left}
          x2={padding.left}
          y1={padding.top}
          y2={h - padding.bottom}
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="1"
        />
        {/* X-axis line */}
        <line
          x1={padding.left}
          x2={w - padding.right}
          y1={h - padding.bottom}
          y2={h - padding.bottom}
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="1"
        />

        {/* Area */}
        {!isEmpty && <path d={areaPath} fill="url(#areaGrad)" />}
        {/* Line */}
        {!isEmpty && (
          <path
            d={path}
            fill="none"
            stroke={accent}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {showDots && !isEmpty &&
          data.map((d, i) => (
            <circle
              key={i}
              cx={xFor(i)}
              cy={yFor(d.value)}
              r="2.5"
              fill="#ffffff"
            />
          ))}

        {/* x ticks */}
        {!isEmpty && xTicks.map((ti) => (
          <text
            key={ti}
            x={xFor(ti)}
            y={h - padding.bottom + 14}
            fontSize="10"
            textAnchor="middle"
            fill="rgba(255,255,255,0.75)"
          >
            {data[ti]?.label || data[ti]?.time || ""}
          </text>
        ))}

        {/* Empty state message centrato dentro l'area chart */}
        {isEmpty && (
          <foreignObject
            x={padding.left + 8}
            y={padding.top + innerH / 2 - 30}
            width={innerW - 16}
            height={60}
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{
                color: "rgba(255,255,255,0.85)",
                fontSize: "12px",
                textAlign: "center",
                fontFamily: "Inter, -apple-system, sans-serif",
                lineHeight: 1.35,
              }}
            >
              {emptyMessage}
            </div>
          </foreignObject>
        )}
      </svg>
      {unit && (
        <div className="absolute top-3 right-4 text-[11px] text-white/80 font-medium">
          {unit}
        </div>
      )}
    </div>
  );
}
